import { useCallback } from 'react';

import { useAuth } from '@/hooks/useAuth';
import { useEmmaContext } from '@/contexts/EmmaContext';
import { setEmmaPageState } from '@/utils/emmaPageState';
import { getTenantScope, getOrganizationIdFromStorage, getActivePartitionIdFromStorage } from '@/utils/authHeaders';
import { hasPermission, PERMISSION_ACTIONS, type PermissionAction } from '@/types/roles';
import { apiRequest } from '@/lib/api';

type Primitive = string | number | boolean | null;

export interface EmmaUiAction {
  action: string;
  selector?: string;
  value?: Primitive;
  values?: Record<string, unknown>;
  summary?: string;
  permission?: string;
  required_permission?: string;
  requiredPermission?: string;
  organization_id?: string;
  org_id?: string;
  partition_id?: string;
  active_partition_id?: string;
  tenant?: {
    organization_id?: string;
    partition_id?: string;
  };
  page_context?: Record<string, unknown>;
  page_state?: Record<string, unknown>;
  context?: Record<string, unknown>;
  event?: string;
  detail?: Record<string, unknown>;
}

export interface EmmaActionExecutionResult {
  action: EmmaUiAction;
  executed: boolean;
  reason?: string;
  error?: string;
}

const EDIT_ACTIONS = new Set(['select', 'fill', 'click', 'set_state', 'update_state']);
const READ_ACTIONS = new Set(['refresh_step_data', 'focus', 'dispatch_event']);

const isPermissionAction = (value: string): value is PermissionAction =>
  PERMISSION_ACTIONS.includes(value as PermissionAction);

const normalizeActionList = (input: unknown): EmmaUiAction[] => {
  if (!input) return [];
  if (Array.isArray(input)) {
    return input.filter((item): item is EmmaUiAction => Boolean(item && typeof item === 'object'));
  }
  if (typeof input === 'object') {
    const maybeAction = input as EmmaUiAction & { actions?: unknown };
    if (Array.isArray(maybeAction.actions)) {
      return normalizeActionList(maybeAction.actions);
    }
    if (typeof maybeAction.action === 'string') {
      return [maybeAction];
    }
  }
  return [];
};

const normalizeScalar = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  const str = String(value).trim();
  return str.length > 0 ? str : null;
};

const setElementValue = (element: Element, value: Primitive): boolean => {
  const normalized = value === null || value === undefined ? '' : String(value);
  if (element instanceof HTMLInputElement) {
    if (element.type === 'checkbox' || element.type === 'radio') {
      element.checked = value === true || normalized === 'true' || normalized === '1' || normalized === element.value;
    } else {
      element.value = normalized;
    }
    element.dispatchEvent(new Event('input', { bubbles: true }));
    element.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  if (element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement) {
    element.value = normalized;
    element.dispatchEvent(new Event('input', { bubbles: true }));
    element.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  return false;
};

export function useEmmaActionExecutor() {
  const { user } = useAuth();
  const { pageContext, setPageContext, updateContext } = useEmmaContext();

  const getRequiredPermission = useCallback((action: EmmaUiAction): PermissionAction | null => {
    const explicit =
      action.requiredPermission || action.required_permission || action.permission || null;
    if (explicit && isPermissionAction(explicit)) {
      return explicit;
    }
    if (READ_ACTIONS.has(action.action)) {
      return 'campaign.view';
    }
    if (EDIT_ACTIONS.has(action.action)) {
      return 'campaign.edit';
    }
    return null;
  }, []);

  const canExecuteAction = useCallback(
    (action: EmmaUiAction): { allowed: boolean; reason?: string } => {
      if (!user) {
        return { allowed: false, reason: 'missing_user' };
      }

      if (!hasPermission(user.role, 'emma.chat')) {
        return { allowed: false, reason: 'missing_emma_permission' };
      }

      const requiredPermission = getRequiredPermission(action);
      if (requiredPermission && !hasPermission(user.role, requiredPermission)) {
        return { allowed: false, reason: `missing_permission:${requiredPermission}` };
      }

      const scope = getTenantScope();
      const expectedOrgId =
        normalizeScalar(action.organization_id) ||
        normalizeScalar(action.org_id) ||
        normalizeScalar(action.tenant?.organization_id);
      const expectedPartitionId =
        normalizeScalar(action.partition_id) ||
        normalizeScalar(action.active_partition_id) ||
        normalizeScalar(action.tenant?.partition_id);

      const activeOrgId = normalizeScalar(pageContext.orgId) || normalizeScalar(scope.organizationId);
      const activePartitionId =
        normalizeScalar(pageContext.partitionId) || normalizeScalar(scope.activePartitionId);

      if (!normalizeScalar(pageContext.userId) || !activeOrgId) {
        return { allowed: false, reason: 'missing_page_context' };
      }

      if (expectedOrgId && activeOrgId && expectedOrgId !== activeOrgId) {
        return { allowed: false, reason: 'organization_mismatch' };
      }

      if (expectedPartitionId && activePartitionId && expectedPartitionId !== activePartitionId) {
        return { allowed: false, reason: 'partition_mismatch' };
      }

      return { allowed: true };
    },
    [getRequiredPermission, pageContext.orgId, pageContext.partitionId, pageContext.userId, user],
  );

  const executeAction = useCallback(
    async (action: EmmaUiAction): Promise<EmmaActionExecutionResult> => {
      const authorization = canExecuteAction(action);
      if (!authorization.allowed) {
        return {
          action,
          executed: false,
          reason: authorization.reason,
        };
      }

      try {
        switch (action.action) {
          case 'refresh_step_data': {
            window.dispatchEvent(new CustomEvent('emma-ui-refresh', { detail: action }));
            return { action, executed: true };
          }
          case 'set_page_context':
          case 'update_page_context': {
            if (action.page_context && typeof action.page_context === 'object') {
              setPageContext(action.page_context as Partial<typeof pageContext>);
              return { action, executed: true };
            }
            return { action, executed: false, reason: 'missing_page_context_payload' };
          }
          case 'set_state':
          case 'update_state': {
            if (action.page_state && typeof action.page_state === 'object') {
              setEmmaPageState(action.page_state);
            }
            if (action.context && typeof action.context === 'object') {
              updateContext(action.context);
            }
            window.dispatchEvent(
              new CustomEvent('emma-ui-state-update', {
                detail: action.detail || action,
              }),
            );
            return { action, executed: true };
          }
          case 'select':
          case 'fill': {
            const selector = normalizeScalar(action.selector);
            if (!selector) return { action, executed: false, reason: 'missing_selector' };
            const element = document.querySelector(selector);
            if (!element) return { action, executed: false, reason: 'target_not_found' };

            const value = action.value ?? '';
            const updated = setElementValue(element, value as Primitive);
            return {
              action,
              executed: updated,
              reason: updated ? undefined : 'unsupported_target_element',
            };
          }
          case 'click': {
            const selector = normalizeScalar(action.selector);
            if (!selector) return { action, executed: false, reason: 'missing_selector' };
            const element = document.querySelector(selector);
            if (!element) return { action, executed: false, reason: 'target_not_found' };
            if (element instanceof HTMLElement) {
              element.click();
              return { action, executed: true };
            }
            return { action, executed: false, reason: 'target_not_clickable' };
          }
          case 'focus': {
            const selector = normalizeScalar(action.selector);
            if (!selector) return { action, executed: false, reason: 'missing_selector' };
            const element = document.querySelector(selector);
            if (!element) return { action, executed: false, reason: 'target_not_found' };
            if (element instanceof HTMLElement) {
              element.focus();
              return { action, executed: true };
            }
            return { action, executed: false, reason: 'target_not_focusable' };
          }
          case 'dispatch_event': {
            const eventName = normalizeScalar(action.event);
            if (!eventName) return { action, executed: false, reason: 'missing_event_name' };
            window.dispatchEvent(new CustomEvent(eventName, { detail: action.detail || action }));
            return { action, executed: true };
          }
          case 'EXECUTE_QUICK_GENERATE': {
            const orgId = getOrganizationIdFromStorage();
            const partitionId = getActivePartitionIdFromStorage();
            
            const asset_type = (
              normalizeScalar(action.value) ||
              normalizeScalar(action.values?.asset_type) ||
              normalizeScalar(action.context?.asset_type) ||
              normalizeScalar(action.page_context?.asset_type) ||
              'battlecard'
            );

            const rawDomain = (action.context?.account_domain || action.values?.account_domain || 'google.com') as string;
            const account_domain = rawDomain.includes('.') ? rawDomain : `${rawDomain}.com`;
            const vertical = (action.context?.vertical || action.values?.vertical || 'technology') as string;
            const persona_role = (action.context?.persona_role || action.values?.persona_role || 'cto') as string;
            const deal_stage = (action.context?.deal_stage || action.values?.deal_stage || 'early') as string;

            const deal_context = {
              account_domain,
              vertical,
              persona_role,
              deal_stage,
              partner_id: partitionId || '',
              requested_assets: [asset_type],
              ...((action.context?.deal_context || action.values?.deal_context || action.context || action.values || {}) as Record<string, unknown>),
            };

            try {
              window.dispatchEvent(new CustomEvent('emma-stateless-start', {
                detail: { asset_type }
              }));
              const response = await apiRequest('/api/v1/personalize/deal', {
                method: 'POST',
                body: JSON.stringify({
                  organization_id: orgId,
                  client_partner_id: partitionId,
                  asset_type,
                  resolution: 'all',
                  deal_context
                })
              });

              if (response.status === 403 || response.status === 422) {
                const securityErrorText = 'I encountered a security restriction. Please ensure your partner context is active.';
                window.dispatchEvent(new CustomEvent('emma-stateless-error', {
                  detail: { message: securityErrorText, asset_type }
                }));
                return { action, executed: false, reason: 'security_restriction', error: securityErrorText };
              }

              if (!response.ok) {
                throw new Error(`API returned status ${response.status}`);
              }

              const payload = await response.json();
              window.dispatchEvent(new CustomEvent('emma-stateless-asset', {
                detail: { asset: payload, asset_type, deal_context }
              }));
              return { action, executed: true };
            } catch (err: any) {
              console.error('Stateless generation failed:', err);
              const errorMsg = err instanceof Error ? err.message : 'Unknown generation error';
              window.dispatchEvent(new CustomEvent('emma-stateless-error', {
                detail: { message: `Generation failed: ${errorMsg}`, asset_type }
              }));
              return { action, executed: false, reason: 'api_error', error: errorMsg };
            }
          }
          case 'OPEN_ARTIFACT_PANEL':
          case 'open_artifact_panel': {
            window.dispatchEvent(new CustomEvent('emma-open-artifact-panel', {
              detail: action
            }));
            return { action, executed: true };
          }
          default:
            return { action, executed: false, reason: 'unsupported_action' };
        }
      } catch (executorError) {
        return {
          action,
          executed: false,
          reason: 'execution_error',
          error: executorError instanceof Error ? executorError.message : 'Unknown action execution error',
        };
      }
    },
    [canExecuteAction, pageContext, setPageContext, updateContext],
  );

  const executeActions = useCallback(
    async (payload: unknown): Promise<EmmaActionExecutionResult[]> => {
      const actions = normalizeActionList(payload);
      if (actions.length === 0) return [];

      const results: EmmaActionExecutionResult[] = [];
      for (const action of actions) {
        // Sequential execution prevents race conditions against shared form state.
        const result = await executeAction(action);
        results.push(result);
      }
      return results;
    },
    [executeAction],
  );

  return {
    executeAction,
    executeActions,
  };
}

export default useEmmaActionExecutor;
